import React from 'react';
import { useNavigate } from 'react-router-dom';
import ProductDesignRenderer from './ProductDesignRenderer';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Product } from '../data/mockData';
import { generateRandomCode } from '../utils/common';

interface ProductCardProps {
  product: Product;
  viewMode: 'grid' | 'list';
  index?: number;
}

const ProductCard: React.FC<ProductCardProps & { hasResource: boolean }> = ({ product, viewMode, hasResource, index }) => {
  const navigate = useNavigate(); 
  const handleCustomizeClick = (e: React.MouseEvent) => {
    e.preventDefault(); 
    // 生成随机码
    let cacheKey = generateRandomCode();
    if (product.code && product.code.startsWith('AI')) {
      cacheKey = product.code;
    } 
    // 创建缓存数据
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 1天后过期

    const cacheData = {
      key: cacheKey,
      product: product,
      timestamp: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      version: '1.0'
    }; 
    localStorage.setItem(`design_${cacheKey}`, JSON.stringify(cacheData));
    
    // 跳转到设计页面
    navigate(`/product/${product.id}/${cacheKey}`);
  };
  if (viewMode === 'grid') {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
        <button onClick={handleCustomizeClick} className="w-full">
          <div className="aspect-square bg-gray-200">
            {product.templates[0] && product.templates[0].design && hasResource && !product.code?.startsWith('AI') ? (
              // 使用ProductDesignRenderer组件
              <ProductDesignRenderer 
                design={product.templates[0].design}
                previews={product.previews}
                containerWidth={400}
                containerHeight={400}
                index={index}
                className="w-full h-full"
              />
            ) : (
              // 如果没有design属性，使用默认图片显示
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}
          </div>
        </button>
        <div className="p-4">
          <button onClick={handleCustomizeClick} className="w-full text-left">
            <h3 
              className="text-lg font-semibold text-gray-900 mb-2 transition-colors"
              style={{ color: 'inherit' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
              onMouseLeave={(e) => e.currentTarget.style.color = ''}
            >
              {(product.name || '').length > 40 
                ? (product.name || '').slice(0, 40) + '…' 
                : (product.name || '')}
            </h3>
          </button>
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {product.description}
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex items-center">
                {Array.from({ length: Math.max(0, Math.min(5, Math.round(product.rating ?? 0))) }).map((_, i) => (
                  <StarIconSolid key={i} className="w-4 h-4 text-yellow-400" />
                ))}
              </div>
              <span className="ml-1 text-xs text-gray-500">({product.rating})</span>
            </div>
            <span className="text-lg font-bold" style={{ color: '#2e3a47' }}>
              ${product.price}
            </span>
          </div>
          <div className="mt-4">
            <button
              onClick={handleCustomizeClick}
              className="w-full text-white py-2 px-4 rounded-lg transition-colors text-center block"
              style={{ backgroundColor: '#2e3a47' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
            >
              Customize
            </button>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex">
        <button onClick={handleCustomizeClick} className="flex-shrink-0">
          <div className="w-32 h-32 bg-gray-200 overflow-hidden">
            {product.templates[0] && product.templates[0].design && hasResource ? (
              <ProductDesignRenderer
                design={product.templates[0].design}
                previews={product.previews}
                containerWidth={400}
                containerHeight={400}
                index={index}
                className="w-full h-full"
              />
            ) : (
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}
          </div>
        </button>
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <button onClick={handleCustomizeClick} className="w-full text-left">
                <h3 
              className="text-lg font-semibold text-gray-900 mb-2 transition-colors"
              style={{ color: 'inherit' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
              onMouseLeave={(e) => e.currentTarget.style.color = ''}
            >
                  {(product.name || '').length > 30 
                    ? (product.name || '').slice(0, 30) + '…' 
                    : (product.name || '')}
                </h3>
              </button>
              <div className="flex items-center">
                <div className="flex items-center">
                  {Array.from({ length: Math.max(0, Math.min(5, Math.round(product.rating ?? 0))) }).map((_, i) => (
                    <StarIconSolid key={i} className="w-4 h-4 text-yellow-400" />
                  ))}
                </div>
                <span className="ml-1 text-xs text-gray-500">({product.rating} • {product.reviews} reviews)</span>
              </div>
            </div>
            <div className="text-right ml-4">
              <div className="text-2xl font-bold mb-2" style={{ color: '#2e3a47' }}>
                ${product.price}
              </div>
              <button
                onClick={handleCustomizeClick}
                className="text-white py-2 px-6 rounded-lg transition-colors inline-block"
                style={{ backgroundColor: '#2e3a47' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
              >
                Customize
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
